import { z } from 'zod';
import { billingIntervalSchema, discountCodeSchema, invoiceStatusSchema, planIdSchema, pricingTypeSchema, refundRequestStatusSchema, } from '../../billing.js';
/**
 * Billing for the whole account (`product/business-rules.md`, Billing): each
 * website's plan and usage this cycle, and the account's invoices, one per
 * cycle with a line for each website. Read-only, and only for Account Users
 * holding the billing and invoices permission. Plans and payments are changed
 * by Groundly through admin commands, never in the dashboard.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-05.');
// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------
export const planSummarySchema = z.object({
    id: planIdSchema,
    name: z.string(),
    includedMessages: z.number().int(),
    /** Page approvals per billing cycle. */
    pagesPerCycle: z.number().int(),
    /** Custom pages at once; 0 when custom data isn't included. */
    customPages: z.number().int(),
    /** Free: the assistant stops at the included AI replies, and nothing is invoiced. */
    stopsAtIncluded: z.boolean(),
    /** The monthly price. */
    listPriceCents: z.number().int(),
    foundingPriceCents: z.number().int(),
    /** The yearly price, paid up front: 10 × the monthly price. */
    listYearlyPriceCents: z.number().int(),
    foundingYearlyPriceCents: z.number().int(),
    /** Each AI reply over the included amount, billed exactly (2¢); never more than the next plan up. */
    overageCentsPerReply: z.number().int(),
});
export const subscriptionSchema = z.object({
    planId: planIdSchema,
    pricingType: pricingTypeSchema,
    /** Always null: founding pricing lasts for life (it was locked for 12 months). Kept for older clients. */
    foundingLockedUntil: isoDate.nullable(),
    /** Whether founding pricing applies to the current cycle. */
    foundingActive: z.boolean(),
    startsOn: isoDate,
    endsOn: isoDate.nullable(),
    /** Paid every month, or once a year up front. */
    billingInterval: billingIntervalSchema,
    /** A yearly plan: the first day of the cycle its fee is next charged in; `null` when monthly or ending first. */
    renewsOn: isoDate.nullable(),
});
export const usageSchema = z.object({
    periodStart: isoDate,
    periodEnd: isoDate,
    /** The first billed day: the cycle's start, or the plan's start in a part cycle. */
    billedFrom: isoDate,
    used: z.number().int(),
    /** The plan's messages for the billed days; `null` while the website has no plan. */
    included: z.number().int().nullable(),
    projected: z.number().int(),
    overageMessages: z.number().int(),
    estimatedOverageCents: z.number().int(),
});
export const billingWarningsSchema = z.object({
    approachingCap: z.boolean(),
    overCap: z.boolean(),
    upgrade: z
        .object({
        planId: planIdSchema,
        planName: z.string(),
        projectedCostCents: z.number().int(),
        nextPlanCostCents: z.number().int(),
    })
        .nullable(),
});
export const websiteBillingSchema = z.object({
    websiteId: z.uuid(),
    websiteName: z.string(),
    /** `null` until Groundly assigns the website a plan. */
    subscription: subscriptionSchema.nullable(),
    plan: planSummarySchema.nullable(),
    usage: usageSchema,
    warnings: billingWarningsSchema,
    /** This website's line on the next invoice so far: plan charge plus overage to date, less any upgrade credit, before discounts. */
    estimatedCents: z.number().int(),
    /** What this website's own discounts take off that line so far. */
    discountCents: z.number().int(),
});
/** A discount Groundly gave the account, as it applies to this cycle. */
export const activeDiscountSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    /** "20% off" or "$25.00 off". */
    description: z.string(),
    /** The websites it covers; `null` when it covers the whole invoice. */
    websiteNames: z.array(z.string()).nullable(),
    /** The last day of the last cycle it applies to; `null` while it has no end. */
    endsOn: isoDate.nullable(),
});
/**
 * The card on file (Paddle): a card every issued invoice is charged to at
 * once, and that a paid plan needs. `unavailable`: card payments aren't set up
 * for Groundly; `off`: no card yet; `on`; `failing`: the card declined the
 * last invoice, or Paddle says its subscription is past due.
 */
export const CARD_STATES = ['unavailable', 'off', 'on', 'failing'];
export const cardStateSchema = z.enum(CARD_STATES);
/** A saved card. Paddle never shares the number, only these details. */
export const savedCardSchema = z.object({
    /** Paddle's id for the saved payment method; null when Paddle didn't say. */
    id: z.string().nullable(),
    /** e.g. `visa`, or the payment method's type when it isn't a card, e.g. `paypal`. */
    brand: z.string(),
    last4: z.string().nullable(),
    expiryMonth: z.number().int().nullable(),
    expiryYear: z.number().int().nullable(),
    /** The card invoices are charged to. It can be replaced but not removed. */
    isDefault: z.boolean(),
});
export const cardOnFileSchema = z.object({
    state: cardStateSchema,
    /** The card invoices are charged to; null until Paddle has reported it. */
    defaultCard: savedCardSchema.nullable(),
});
export const accountBillingSchema = z.object({
    period: z.object({ start: isoDate, end: isoDate }),
    /** When this cycle's invoice is issued: the 5th after it ends. */
    nextInvoiceOn: isoDate,
    /** Every website's lines so far, added up, less discounts. */
    estimatedTotalCents: z.number().int(),
    /** What every discount takes off this cycle so far. */
    estimatedDiscountCents: z.number().int(),
    /** The discounts that apply to this cycle. */
    discounts: z.array(activeDiscountSchema),
    websites: z.array(websiteBillingSchema),
    /** Every plan, for the plans table. */
    plans: z.array(planSummarySchema),
    card: cardOnFileSchema,
});
export const invoiceSummarySchema = z.object({
    id: z.uuid(),
    /** `INV-000123`. */
    number: z.string(),
    periodStart: isoDate,
    periodEnd: isoDate,
    websiteCount: z.number().int(),
    baseCents: z.number().int(),
    overageCents: z.number().int(),
    /** Credit for the unused months of yearly fees replaced by an upgrade. */
    creditCents: z.number().int(),
    /** Every discount on the invoice: websites' and the whole invoice's. */
    discountCents: z.number().int(),
    /** Base plus overage, less credits and discounts: what is owed. */
    totalCents: z.number().int(),
    status: invoiceStatusSchema,
    issuedAt: z.iso.datetime(),
    /** Unpaid after this day, the invoice is overdue and the account's services stop. */
    dueOn: isoDate,
    paidAt: z.iso.datetime().nullable(),
});
/** One website's charge on an invoice, copied when the invoice was issued. */
export const invoiceLineSchema = z.object({
    websiteId: z.uuid(),
    websiteName: z.string(),
    planId: planIdSchema,
    planName: z.string(),
    pricingType: pricingTypeSchema,
    billingInterval: billingIntervalSchema,
    listPriceCents: z.number().int(),
    /** The plan's price this line was charged at (founding or list): monthly, or yearly on a yearly plan. */
    priceCents: z.number().int(),
    billedFrom: isoDate,
    billedTo: isoDate,
    billedDays: z.number().int(),
    periodDays: z.number().int(),
    includedMessages: z.number().int(),
    messagesUsed: z.number().int(),
    /** The plan's charge: the monthly price, or a yearly fee in the cycle it is due, else 0. */
    baseCents: z.number().int(),
    overageMessages: z.number().int(),
    /** What each reply over was charged at. */
    overageCentsPerReply: z.number().int(),
    overageCents: z.number().int(),
    /** Only on lines from before overage was uncapped (2026-11-01); always `null` now. */
    cappedAtPlanName: z.string().nullable(),
    /** Credit for the unused months of the yearly fee an upgrade replaced. */
    creditCents: z.number().int(),
    /** This website's own discounts. */
    discountCents: z.number().int(),
    /** Base plus overage, less the credit and this website's discounts. */
    totalCents: z.number().int(),
});
/** One discount on an invoice, copied when the invoice was issued. */
export const invoiceDiscountSchema = z.object({
    label: z.string(),
    description: z.string(),
    /** The website's line it came off; `null` for a discount on the whole invoice. */
    websiteId: z.uuid().nullable(),
    websiteName: z.string().nullable(),
    amountCents: z.number().int(),
});
export const invoiceSchema = invoiceSummarySchema.extend({
    /**
     * Unpaid, at least $1.00, card payments are set up, and it isn't already
     * being charged to the card on file: the invoice can be paid by card.
     */
    payableByCard: z.boolean(),
    /** Unpaid and charged to the card on file: Paddle is confirming the payment. */
    autopayPending: z.boolean(),
    paymentReference: z.string().nullable(),
    lines: z.array(invoiceLineSchema),
    discounts: z.array(invoiceDiscountSchema),
});
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
export const accountStandingSchema = z.object({
    /** An invoice is unpaid past its due date: only Billing runs until it is paid. */
    restricted: z.boolean(),
    overdueInvoices: z.array(z.object({
        id: z.uuid(),
        /** `INV-000123`. */
        number: z.string(),
        totalCents: z.number().int(),
        dueOn: isoDate,
    })),
});
/**
 * Whether the account is restricted by an overdue invoice. Any Account User,
 * and it runs while restricted, so every dashboard can show the restricted
 * view (`patterns/auth-patterns.md`, 2).
 */
export const getAccountStanding = {
    method: 'GET',
    path: '/api/dashboard/billing/standing',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: accountStandingSchema,
};
export const getAccountBilling = {
    method: 'GET',
    path: '/api/dashboard/billing',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: accountBillingSchema,
};
/** The plans on offer, with their current prices, for choosing one when adding a website. */
export const listPlans = {
    method: 'GET',
    path: '/api/dashboard/plans',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ items: z.array(planSummarySchema) }),
};
export const listInvoices = {
    method: 'GET',
    path: '/api/dashboard/billing/invoices',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ items: z.array(invoiceSummarySchema) }),
};
export const getInvoice = {
    method: 'GET',
    path: '/api/dashboard/billing/invoices/:invoiceId',
    params: z.object({ invoiceId: z.uuid() }),
    query: z.object({}),
    body: z.object({}),
    response: invoiceSchema,
};
/**
 * A Paddle checkout for the dashboard to open with Paddle.js: the
 * transaction, and the client-side token and environment to open it with.
 * With `savedPaymentMethodId`, the checkout offers that saved card.
 */
export const cardCheckoutSchema = z.object({
    transactionId: z.string(),
    /** The account's Paddle customer (`ctm_…`), for Paddle Retain (`pwCustomer`). */
    customerId: z.string().nullable(),
    clientToken: z.string(),
    environment: z.enum(['sandbox', 'production']),
    customerAuthToken: z.string().optional(),
    savedPaymentMethodId: z.string().optional(),
});
/**
 * What the dashboard needs to load Paddle.js on its own: for a checkout link
 * Paddle sent (the default payment link with `?_ptxn=`, e.g. in an email to
 * update a card). `settings` is null while card payments aren't set up. Needs the billing
 * and invoices permission.
 */
export const getCheckoutSettings = {
    method: 'GET',
    path: '/api/dashboard/billing/checkout-settings',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.object({
        settings: z
            .object({
            clientToken: z.string(),
            environment: z.enum(['sandbox', 'production']),
            /** The account's Paddle customer, for Paddle Retain; null before its first payment. */
            customerId: z.string().nullable(),
        })
            .nullable(),
    }),
};
/**
 * Opens a card payment (a Paddle checkout) for an unpaid invoice's total; the
 * invoice shows as paid once Paddle reports the payment. Needs the billing
 * and invoices permission.
 */
export const startInvoicePayment = {
    method: 'POST',
    path: '/api/dashboard/billing/invoices/:invoiceId/payment',
    params: z.object({ invoiceId: z.uuid() }),
    query: z.object({}),
    body: z.object({}),
    response: cardCheckoutSchema,
};
/** The account's saved cards, the default first. Needs the billing and invoices permission. */
export const listCards = {
    method: 'GET',
    path: '/api/dashboard/billing/cards',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ items: z.array(savedCardSchema) }),
};
/**
 * Adds a card: the checkout that saves it. The account's first card starts
 * the card on file; a later one replaces the default. Needs the billing and
 * invoices permission.
 */
export const addCard = {
    method: 'POST',
    path: '/api/dashboard/billing/cards',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: cardCheckoutSchema,
};
/**
 * Makes a saved card the default: the checkout where the payer confirms it.
 * Needs the billing and invoices permission.
 */
export const makeCardDefault = {
    method: 'POST',
    path: '/api/dashboard/billing/cards/:cardId/default',
    params: z.object({ cardId: z.string().min(1).max(100) }),
    query: z.object({}),
    body: z.object({}),
    response: cardCheckoutSchema,
};
/**
 * Removes a saved card. The default card can't be removed: make another card
 * the default first. Needs the billing and invoices permission.
 */
export const removeCard = {
    method: 'DELETE',
    path: '/api/dashboard/billing/cards/:cardId',
    params: z.object({ cardId: z.string().min(1).max(100) }),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ items: z.array(savedCardSchema) }),
};
/**
 * What stops the account putting a website on a paid plan, checked in this
 * order: unpaid invoices nobody is collecting, then no working card. `none`
 * when nothing does, or when card payments aren't set up for Groundly.
 */
export const PAID_PLAN_BLOCKERS = [
    'none',
    'unpaid_invoices',
    'card_required',
    'card_failing',
];
export const paidPlanCheckSchema = z.object({
    blocker: z.enum(PAID_PLAN_BLOCKERS),
    /** With `unpaid_invoices`: the invoices to pay, oldest first. */
    unpaidInvoices: z.array(z.object({ id: z.uuid(), number: z.number().int(), totalCents: z.number().int() })),
});
/** Asked before a paid plan is chosen. Needs the website setup permission. */
export const getPaidPlanCheck = {
    method: 'GET',
    path: '/api/dashboard/billing/paid-plan-check',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: paidPlanCheckSchema,
};
// ---------------------------------------------------------------------------
// One website's plan and usage (its Settings page)
// ---------------------------------------------------------------------------
/** One past or current cycle of a website's usage. */
export const usageCycleSchema = z.object({
    periodStart: isoDate,
    periodEnd: isoDate,
    used: z.number().int(),
    /** The plan's messages for the days billed; `null` when the website had no plan. */
    included: z.number().int().nullable(),
    planName: z.string().nullable(),
});
export const websitePlanSchema = websiteBillingSchema.extend({
    /**
     * The website's one discount this cycle: the newest that covers it, its own
     * or the whole invoice's. While it has one, it is billed at the list price.
     */
    discount: z
        .object({
        label: z.string(),
        description: z.string(),
        /** The code it was redeemed from, or null when Groundly gave it. */
        code: z.string().nullable(),
        endsOn: isoDate.nullable(),
    })
        .nullable(),
    /** A change already chosen, starting with the next cycle. */
    nextSubscription: subscriptionSchema.nullable(),
    /** This cycle first, then up to five earlier cycles. */
    history: z.array(usageCycleSchema),
    plans: z.array(planSummarySchema),
    /**
     * Set while the website's yearly payment was made within the last 14 days
     * and no refund was asked for it yet: cancelling now may ask for it back.
     */
    yearlyRefund: z
        .object({
        amountCents: z.number().int(),
        /** The last moment a refund can be asked for. */
        availableUntil: z.iso.datetime(),
    })
        .nullable(),
    /** The website's newest refund request, if any. */
    refundRequest: z
        .object({
        status: refundRequestStatusSchema,
        amountCents: z.number().int(),
        requestedAt: z.iso.datetime(),
        decidedAt: z.iso.datetime().nullable(),
    })
        .nullable(),
});
export const cancelWebsitePlanBodySchema = z.object({
    /** Ask for the yearly payment back: only while `yearlyRefund` is set. */
    refund: z.boolean().default(false),
});
/**
 * Cancels the website's paid plan: it moves to Free when the plan ends, after
 * this billing cycle on a monthly plan or at renewal on a yearly one. With
 * `refund`, a yearly plan paid for within the last 14 days also asks for that
 * payment back; Groundly checks the request, and approving it ends the plan
 * at once. Needs the billing and invoices permission.
 */
export const cancelWebsitePlan = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/plan/cancel',
    params: z.object({ websiteId: z.uuid() }),
    query: z.object({}),
    body: cancelWebsitePlanBodySchema,
    response: websitePlanSchema,
};
const websiteParams = z.object({ websiteId: z.uuid() });
/** Needs the website setup permission (it's part of the website's Settings). */
export const getWebsitePlan = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/plan',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: websitePlanSchema,
};
export const changeWebsitePlanBodySchema = z.object({
    planId: planIdSchema,
    /** Monthly or yearly; left out, the website keeps how it pays now (monthly for a first plan). */
    billingInterval: billingIntervalSchema.optional(),
});
/**
 * Upgrades or downgrades a website's plan, or switches between monthly and
 * yearly. On a monthly plan an upgrade applies now, and the whole cycle is
 * billed at the new plan with no proration; a downgrade starts with the next
 * cycle. A website without a plan starts today. Moving to yearly without an
 * upgrade starts with the next cycle (today's part cycle is billed monthly);
 * an upgrade to yearly starts the year with this cycle. On a yearly plan, an upgrade
 * applies now with the unused months credited; a downgrade or a switch to
 * monthly applies at renewal. Founding or list pricing stays as Groundly set
 * it. Needs the billing and invoices permission.
 */
export const changeWebsitePlan = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/plan',
    params: websiteParams,
    query: z.object({}),
    body: changeWebsitePlanBodySchema,
    response: websitePlanSchema,
};
export const redeemDiscountCodeBodySchema = z.object({ code: discountCodeSchema });
/**
 * Gives a website a discount from a code, from this billing cycle. It
 * replaces the website's founding price and any other discount on it. Needs
 * the billing and invoices permission.
 */
export const redeemDiscountCode = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/discount-code',
    params: websiteParams,
    query: z.object({}),
    body: redeemDiscountCodeBodySchema,
    response: websitePlanSchema,
};
export const redeemAccountDiscountCodeBodySchema = z.object({
    code: discountCodeSchema,
    /** One website of the account; `null` for the whole invoice. */
    websiteId: z.uuid().nullable(),
});
/**
 * A discount code from the account's Billing page, for one website or the
 * whole invoice, from this billing cycle. It replaces the founding price and
 * any other discount on what it covers. Needs the billing and invoices
 * permission.
 */
export const redeemAccountDiscountCode = {
    method: 'POST',
    path: '/api/dashboard/billing/discount-code',
    params: z.object({}),
    query: z.object({}),
    body: redeemAccountDiscountCodeBodySchema,
    response: accountBillingSchema,
};
//# sourceMappingURL=billing.js.map